import { hc } from 'hono/client';
import type { AppType } from '@minutes-system/backend/src/app';
import { API_BASE_URL } from '../../../config/env';
import authAtom from '../authAtom';
import buildHttpError from '../../../shared/httpError';
import refreshSession from '../refresh/refreshSession';
import { store } from '../../../store';

// refresh の多重実行を防ぐための単一フライト Promise
let refreshInFlight: Promise<boolean> | null = null;

const publicFetch: typeof fetch = (input, init) => fetch(input, { ...init, credentials: 'include' });

export const publicClient = hc<AppType>(API_BASE_URL, { fetch: publicFetch });

const ensureRefreshed = async (reason: string): Promise<boolean> => {
  if (refreshInFlight) {
    return refreshInFlight;
  }
  const session = store.get(authAtom);
  if (!session) {
    return false;
  }
  refreshInFlight = (async () => {
    try {
      const newAccessToken = await refreshSession(publicClient);
      store.set(authAtom, prev => (prev ? { ...prev, accessToken: newAccessToken } : prev));
      return true;
    } catch (e) {
      console.warn('refresh failed', reason, e);
      store.set(authAtom, null);
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
};

const guardedFetch: typeof fetch = async (input, init) => {
  const doFetch = () => {
    const current = store.get(authAtom);
    const headers = new Headers(init?.headers);
    if (current?.accessToken) {
      headers.set('Authorization', `Bearer ${current.accessToken}`);
    }
    return fetch(input, { ...init, headers, credentials: 'include' });
  };

  let res = await doFetch();
  if (res.ok) {
    return res;
  }
  if (res.status !== 401) {
    throw await buildHttpError(res);
  }
  if (!store.get(authAtom)) {
    return res;
  } // 既に未ログイン
  const ok = await ensureRefreshed('reactive-401');
  if (!ok) {
    return res;
  }
  res = await doFetch();
  if (res.ok) {
    return res;
  }
  throw await buildHttpError(res);
};

export const guardedClient = hc<AppType>(API_BASE_URL, { fetch: guardedFetch });

export const triggerRefresh = (reason: string) => ensureRefreshed(reason);
